import { get } from "lodash"
import React, { Suspense, lazy, useEffect } from "react"
import { useIntl } from "react-intl"
import { useAuth } from "react-oidc-context"
import { useSelector } from "react-redux"
import { Redirect, Route, Switch, useHistory, useLocation } from "react-router-dom"
import { Loading } from "~/components/DetailsForms"
import SnackbarManager from "~/components/SnackbarManager"
import Spinner from "~/components/Spinner/Spinner"
import { Routes } from "~/constants/routes"
import useDocumentTitle from "~/hooks/useDocumentTitle"
import Autologout from "~/pages/Autologout/Autologout"
import Login from "~/pages/Login"
import LoadingPage from "~/pages/Login/LoadingPage"
import Logout from "~/pages/Logout"
import checkVersion from "./versionUpdate"
import BackendStarting from "~/pages/Login/BackendStarting"

const PlaneusTv = lazy(() => import("~/pages/PlaneusTv/PlaneusTv"))
const Terminal = lazy(() => import("~/pages/Terminal/Terminal"))
const VideoTutorials = lazy(() => import("~/pages/VideoTutorials/VideoTutorials"))
const ResetPassword = lazy(() => import("~/pages/Login/ResetPassword/ResetPassword"))
const ChangeLogs = lazy(() => import("~/pages/ChangeLogs/ChangeLogs"))
const LicenceOverviewReadOnly = lazy(() => import("~/pages/LicenceOverviewReadOnly/LicenceOverviewReadOnly"))
const ForgotPassword = lazy(() => import("~/pages/Login/ResetPassword/ForgotPassword"))
const FakeUser = lazy(() => import("~/pages/FakeUser"))
const AuthenticatedApp = lazy(() => import("../AuthenticatedApp"))

const isSSOCustomer = process.env.REACT_APP_SSOCUSTOMER === "true"

const App = React.memo(() => {
  const history = useHistory()
  const intl = useIntl()
  const location = useLocation()
  const { authenticated, isLoaded, user, isPendingSSO, isPendingCheckToken, backendStarting, salesRecover } =
    useSelector(state => state.user.authentication)
  const { activeNavigator, isLoading, clearStaleState } = useAuth()
  useDocumentTitle(intl.formatMessage({ id: "term.planeus", defaultMessage: "planeus" }))

  useEffect(() => {
    const isFakeUser = get(user, "isFakeUser", false)
    if (isFakeUser && location.pathname.indexOf("planeustvscreen") === -1) {
      history.push(Routes.FAKE_USER)
    }
  }, [authenticated])

  useEffect(() => {
    checkVersion()
    clearStaleState()

    const interval = setInterval(checkVersion, 1000 * 60 * 5)
    return () => clearInterval(interval)
  }, [])

  if (isSSOCustomer && !isLoaded && isPendingCheckToken) return null

  const isPending = isLoading || !!activeNavigator || isPendingSSO || isPendingCheckToken

  return (
    <Suspense
      fallback={
        <Loading>
          <Spinner />
        </Loading>
      }
    >
      <Switch>
        <Route component={Logout} path={Routes.LOGOUT} />
        <Route component={Autologout} exact path={[Routes.AUTO_LOGOUT_TERMINAL, Routes.AUTO_LOGOUT]} />
        <Route component={PlaneusTv} path={Routes.TVSCREEN} />
        <Route component={Terminal} path={Routes.TERMINAL} />
        <Route component={VideoTutorials} path={Routes.VIDEO_TUTORIALS} />
        <Route component={ChangeLogs} path={Routes.CHANGE_LOGS} />
        <Route component={LicenceOverviewReadOnly} path={Routes.LICENCE_OVERVIEW_READ_ONLY} />
        <Route component={FakeUser} path={Routes.FAKE_USER} />
        {(backendStarting || salesRecover) && <Route component={BackendStarting} />}
        {isPending && <Route component={LoadingPage} />}
        {authenticated && <Route component={AuthenticatedApp} />}
        <Route component={Login} path={Routes.LOGIN} />
        <Route component={ForgotPassword} path={Routes.FORGOT_PASSWORD} />
        <Route component={ResetPassword} path={Routes.RESET_PASSWORD} />
        <Redirect push to={Routes.LOGIN} />
      </Switch>
      <SnackbarManager />
    </Suspense>
  )
})

export default App
